import { defineStore } from 'pinia'
import { ref } from '@nuxtjs/composition-api'
import {
  IOrder,
  IOrderData,
  IOrderElement,
  IFetchDivideCheck,
  IFetchOrderPayload,
  IChekTransactionResponse,
  ICheckOrderPositionPayload,
  ISelectOrderPositionPayload
} from './types'
import {
  fetch,
  checkDivideBill,
  checkOrderPosition as checkOrderPositionApi,
  checkTransaction as checkTransactionApi
} from './../api'
import { usePreloaderModel } from '@shared/model/preloader'

export const useOrderModel = defineStore('order', () => {
  const orderData = ref<IOrderData | null>(null)
  const orderElements = ref<IOrderElement[]>([])
  const divideCheck = ref<boolean>(true)
  const transactionStatus = ref<boolean>(false)
  const transactionData = ref<IChekTransactionResponse | null>(null)

  const fetchOrder = async (payload: IFetchOrderPayload) => {
    const { tableId, domen, session, guest, browserId, orderGuid, timeout } = payload
    const res: IOrder = await fetch(
      tableId,
      domen,
      session && session.toString(),
      guest,
      browserId,
      orderGuid,
      timeout
    )

    if (!res.tips) {
      res.tips = {}
    }

    // @ts-ignore
    res.tips.waiter = {
      ...res.waiter
    }

    updateOrder(res)
  }

  // Применяет ответ только для активного заказа той же сессии, иначе текущее состояние не трогаем.
  // 'changed' — пока шёл запрос, состояние обновилось (сокет и т.п.), ответ устарел
  const refreshOrder = async (
    payload: IFetchOrderPayload,
    canApply: () => boolean
  ): Promise<'applied' | 'skipped' | 'changed'> => {
    const { tableId, domen, session, guest, browserId, orderGuid } = payload
    const dataBefore = orderData.value
    const elementsBefore = orderElements.value
    const res: IOrder = await fetch(
      tableId,
      domen,
      session && session.toString(),
      guest,
      browserId,
      orderGuid
    )

    if (orderData.value !== dataBefore || orderElements.value !== elementsBefore) {
      return 'changed'
    }

    if (
      !res?.active ||
      !res.session ||
      !dataBefore?.session ||
      String(res.session) !== String(dataBefore.session) ||
      !canApply()
    ) {
      return 'skipped'
    }

    if (!res.tips) {
      res.tips = {}
    }

    // @ts-ignore
    res.tips.waiter = {
      ...res.waiter
    }

    updateOrder(res)
    return 'applied'
  }

  const updateOrder = (payload: IOrder) => {
    const { guest } = payload
    const { ordersElementAll, ...data } = payload
    const ordersElementAllWithLoading =
    ordersElementAll && ordersElementAll.length
      ? ordersElementAll.map((i) => {
        return {
          ...i,
          loading: false,
          selected:
              guest !== null &&
              i.selected_guest_id === guest &&
              i.status === 'NOT_PAID'
        }
      })
      : []
    const prepayment =
      !!data.prepayment && data.prepayment > 0 ? data.prepayment / 100 : data.prepayment

    orderData.value = {
      ...data,
      prepayment,
      session: data.session ? data.session.toString() : null
    }
    orderElements.value = ordersElementAllWithLoading
  }

  const selectOrderPosition = async (payload: ISelectOrderPositionPayload) => {
    const { id, status, guest, transactionReset, timeout } = payload
    const preloaderModel = usePreloaderModel()

    try {
      const res: boolean = await checkOrderPositionApi(
        id,
        status,
        guest,
        transactionReset,
        timeout
      )

      if (res) {
        if (Array.isArray(id)) {
          id.forEach((i: number) => {
            const selectedOrderElements = orderElements.value.map(order =>
              order.id === Number(i)
                ? {
                    ...order,
                    selected: !status,
                    loading: false,
                    selected_guest_id: !status ? guest : null
                  }
                : order
            )
            orderElements.value = selectedOrderElements
          })
        } else {
          const selectedOrderElements = orderElements.value.map(order =>
            order.id === Number(id)
              ? {
                  ...order,
                  selected: !status,
                  loading: false,
                  selected_guest_id: !status ? guest : null
                }
              : order
          )
          orderElements.value = selectedOrderElements
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
      preloaderModel.status = false
    }
  }

  const checkOrderPosition = (payload: ICheckOrderPositionPayload) => {
    const changedOrders = orderElements.value.map((order, index) =>
      order.id === Number(payload.positions_id)
        ? {
            ...order,
            selected_guest_id: payload.check_status ? Number(payload.guest) : null,
            loading: false,
            selected: payload.isArr ? false : divideCheck.value ? orderElements.value[index].selected : !orderElements.value[index].selected
          }
        : order
    )
    orderElements.value = changedOrders
  }

  const loadingOrderElement = (id: string | number) => {
    const copyOrderElements = orderElements.value.map((i) => {
      return {
        ...i,
        loading: i.id === Number(id) ? !i.loading : i.loading
      }
    })
    orderElements.value = copyOrderElements
  }

  const payedOrderElement = (array: IOrderElement[]) => {
    const copyOrderElements: IOrderElement[] = []

    orderElements.value.forEach((i) => {
      const item = { ...i }
      array.forEach((j: IOrderElement) => {
        if (item.id === j.id) {
          item.status = 'PAID'
        }
      })
      copyOrderElements.push(item)
    })
    orderElements.value = copyOrderElements
  }

  const checkTransaction = async <T>(id: T) => {
    const checkTransaction: IChekTransactionResponse = await checkTransactionApi(id)

    if (checkTransaction?.errors?.code === 404) {
      return false
    }

    // для SBP
    if (
      checkTransaction &&
      checkTransaction.transaction_paid_position_ids
    ) {
      const copyOrderElements: IOrderElement[] = []
      orderElements.value.forEach((i) => {
        const item = { ...i }
        checkTransaction.transaction_paid_position_ids.forEach((j) => {
          if (item.id === j.id) {
            item.status = 'PAID'
          }
        })
        copyOrderElements.push(item)
      })
      orderElements.value = copyOrderElements
    }
    // -----
    transactionData.value = checkTransaction
    transactionStatus.value = checkTransaction?.status
    return checkTransaction
  }

  const fetchDevideCheck = async (id: number) => {
    const diffPayStatus: IFetchDivideCheck = await checkDivideBill(id)
    divideCheck.value = diffPayStatus.diff_pay
  }

  const clearOrderData = () => {
    orderData.value = null
    orderElements.value = []
    transactionStatus.value = false
  }

  return {
    orderData,
    orderElements,
    transactionStatus,
    transactionData,
    divideCheck,
    fetchOrder,
    refreshOrder,
    selectOrderPosition,
    checkOrderPosition,
    loadingOrderElement,
    payedOrderElement,
    checkTransaction,
    fetchDevideCheck,
    clearOrderData,
    updateOrder
  }
})
